import React, { useState, useEffect, useCallback, type CSSProperties, type KeyboardEvent } from "react";
import { buildAgentApiBaseUrl, buildAgentConfigureUrl } from "./agentRuntimeEnv";
import type {
  AgentTaskType,
  AgentTestResult,
  AgentTool,
  AgentToolType,
  AvroField,
  AvroFieldDef,
  AvroSchemaRecord,
  CipEventRecord,
  CipServiceRecord,
  EgressSchemaInfo,
  JoltMappings,
  KbUploadEntry,
  RegisteredAgentConfig,
  RegisteredAgentSummary,
  TaskTemplateMap,
  CipServiceOperation,
} from "./agentStudioTypes";

function getErrorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/* ═══════════════════════════════════════════════════════════════
   CIP AI AGENT STUDIO
   ═══════════════════════════════════════════════════════════════
   - Fetches existing CIP services (dropdown for tool selection)
   - Configure agent: prompt, task type, tools from services, output schema
   - Test agent with sample payload
   - Save as a new CIP service (registers in both agent runtime + CIX)
   ═══════════════════════════════════════════════════════════════ */

const TASK_TYPES = ["CLASSIFY", "EXTRACT", "VALIDATE", "DECIDE", "ENRICH", "ASSESS", "TRANSFORM"];

// ── CIP Platform Theme (matches Cloud Intelligence UI) ──
const S: Record<string, CSSProperties> = {
  app: { fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif", background: "#f4f5f7", color: "#1a1a2e", display: "flex", flexDirection: "column", flex: 1, minHeight: 0, width: "100%" },
  topBar: { background: "#1b2a4a", padding: "10px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  topBarLogo: { fontSize: 14, fontWeight: 600, color: "#fff", display: "flex", alignItems: "center", gap: 10 },
  topBarRight: { display: "flex", alignItems: "center", gap: 16, fontSize: 12, color: "#ccc" },
  navBar: { background: "#fff", borderBottom: "1px solid #e0e0e0", padding: "0 24px", display: "flex", alignItems: "center", gap: 24 },
  navItem: { padding: "12px 0", fontSize: 13, fontWeight: 500, color: "#555", cursor: "pointer", borderBottom: "3px solid transparent" },
  navItemActive: { padding: "12px 0", fontSize: 13, fontWeight: 600, color: "#1b2a4a", cursor: "pointer", borderBottom: "3px solid #2e7d32" },
  header: { background: "#fff", borderBottom: "1px solid #e0e0e0", padding: "12px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  main: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0, flex: 1, minHeight: 480, height: "calc(100vh - 240px)" },
  panel: { padding: "20px 24px", overflowY: "auto", background: "#fff", borderRight: "1px solid #e0e0e0" },
  panelR: { padding: "20px 24px", overflowY: "auto", background: "#fafbfc" },
  section: { marginBottom: 20 },
  label: { display: "block", fontSize: 11, fontWeight: 600, color: "#666", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 5 },
  input: { width: "100%", padding: "9px 12px", background: "#fff", border: "1px solid #d0d0d0", borderRadius: 6, color: "#1a1a2e", fontSize: 13, outline: "none", boxSizing: "border-box" },
  textarea: { width: "100%", padding: "10px 12px", background: "#fff", border: "1px solid #d0d0d0", borderRadius: 6, color: "#1a1a2e", fontSize: 12, fontFamily: "inherit", outline: "none", resize: "vertical", boxSizing: "border-box", lineHeight: 1.5 },
  select: { width: "100%", padding: "9px 12px", background: "#fff", border: "1px solid #d0d0d0", borderRadius: 6, color: "#1a1a2e", fontSize: 13, outline: "none", boxSizing: "border-box", cursor: "pointer" },
  btn: { padding: "9px 18px", border: "none", borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: "pointer", transition: "all 0.2s" },
  btnPrimary: { background: "#1b2a4a", color: "#fff" },
  btnSuccess: { background: "#2e7d32", color: "#fff" },
  btnDanger: { background: "#c62828", color: "#fff" },
  btnGhost: { background: "transparent", border: "1px solid #d0d0d0", color: "#555" },
  chip: { display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", background: "#f0f1f3", border: "1px solid #e0e0e0", borderRadius: 16, fontSize: 12, color: "#444", margin: "3px" },
  chipRemove: { background: "none", border: "none", color: "#c62828", cursor: "pointer", fontSize: 14, padding: 0, lineHeight: 1 },
  card: { background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8, padding: 14, marginBottom: 10 },
  tag: { display: "inline-block", padding: "2px 10px", borderRadius: 10, fontSize: 11, fontWeight: 600 },
  codeBlock: { background: "#f8f9fa", border: "1px solid #e0e0e0", borderRadius: 6, padding: 12, fontSize: 11, fontFamily: "'Consolas', 'Monaco', monospace", color: "#2e7d32", whiteSpace: "pre-wrap", overflowX: "auto", maxHeight: 280, overflowY: "auto" },
  statusBar: { padding: "6px 16px", fontSize: 11, borderTop: "1px solid #e0e0e0", background: "#fff" },
  row: { display: "flex", gap: 12, alignItems: "center" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  divider: { borderTop: "1px solid #e8e8e8", margin: "16px 0" },
  empty: { color: "#999", fontStyle: "italic", fontSize: 12, padding: 16, textAlign: "center" },
};

const activateOnKey = (action: () => void) => (event: KeyboardEvent) => {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    action();
  }
};

/** Parse Avro schema fields recursively (handles nested records). */
const parseAvroFields = (schema: AvroSchemaRecord, prefix = ""): AvroField[] => {
  const fields = schema.fields || [];
  let result: AvroField[] = [];
  for (const field of fields) {
    const fullName = prefix ? `${prefix}.${field.name}` : field.name;
    let fieldType = field.type;

    if (Array.isArray(fieldType)) {
      fieldType = fieldType.find(t => t !== "null") || fieldType[0];
    }

    const complexType =
      typeof fieldType === "object" && fieldType !== null
        ? (fieldType as { type?: string; fields?: AvroFieldDef[]; values?: unknown; items?: unknown })
        : null;

    if (complexType?.type === "record" && complexType.fields) {
      const nestedSchema: AvroSchemaRecord = { type: "record", fields: complexType.fields };
      result.push({ name: fullName, type: "record", subFields: parseAvroFields(nestedSchema, fullName) });
      result = [...result, ...parseAvroFields(nestedSchema, fullName)];
    } else if (complexType?.type === "map") {
      result.push({ name: fullName, type: `map<${complexType.values || "string"}>` });
    } else if (complexType?.type === "array") {
      result.push({ name: fullName, type: `array<${JSON.stringify(complexType.items)}>` });
    } else {
      result.push({ name: fullName, type: typeof fieldType === "string" ? fieldType : JSON.stringify(fieldType) });
    }
  }
  return result;
};

export interface CipAiAgentStudioProps {
  authToken?: string;
  /** Public URL RAER/CIX should call for agent execution (defaults to agent runtime base). */
  agentPublicBaseUrl?: string;
}

const CipAiAgentStudio = ({
  authToken = '',
  agentPublicBaseUrl,
}: CipAiAgentStudioProps) => {
  const CP_API_BASE = `${process.env.REACT_APP_API_CONTROL_PLANE_BASE_URL}`;
  const AGENT_API_BASE = buildAgentApiBaseUrl(CP_API_BASE);
  const AGENT_CONFIGURE_URL = buildAgentConfigureUrl(CP_API_BASE);
  const AGENT_PUBLIC_BASE =
    agentPublicBaseUrl
    || AGENT_API_BASE
    || (typeof window !== 'undefined' ? window.location.origin : '');
  const getToken = useCallback(async () => authToken, [authToken]);
  // ── Agent config state ──
  const [agentName, setAgentName] = useState("");
  const [task, setTask] = useState<AgentTaskType>("DECIDE");
  const [instructions, setInstructions] = useState("");
  const [outputSchema, setOutputSchema] = useState('{\n  "decision": "string",\n  "confidence": 0.0,\n  "reasoning": "string"\n}');
  const [context, setContext] = useState('{\n  "approvalThreshold": 75,\n  "autoRejectBelow": 30,\n  "reviewRange": [30, 75],\n  "escalationRequired": ["HIGH_VALUE", "PEP"]\n}');
  const [maxIter, setMaxIter] = useState(5);
  const [failOpen, setFailOpen] = useState(false);

  // ── Task-specific templates ──
  const TASK_TEMPLATES: TaskTemplateMap = {
    CLASSIFY: {
      context: '{\n  "categories": ["TYPE_A", "TYPE_B", "TYPE_C"],\n  "defaultCategory": "UNKNOWN",\n  "minConfidence": 0.7\n}',
      output: '{\n  "classification": "string",\n  "confidence": 0.0,\n  "reasoning": "string"\n}'
    },
    EXTRACT: {
      context: '{\n  "requiredFields": ["name", "address", "id"],\n  "dateFormat": "YYYY-MM-DD",\n  "defaultCountry": "US"\n}',
      output: '{\n  "extractedFields": {},\n  "missingFields": [],\n  "confidence": 0.0\n}'
    },
    VALIDATE: {
      context: '{\n  "minAge": 18,\n  "requiredDocuments": ["ID", "PROOF_OF_ADDRESS"],\n  "validIdTypes": ["PASSPORT", "DRIVERS_LICENSE", "NATIONAL_ID"]\n}',
      output: '{\n  "valid": true,\n  "errors": [],\n  "warnings": []\n}'
    },
    DECIDE: {
      context: '{\n  "approvalThreshold": 75,\n  "autoRejectBelow": 30,\n  "reviewRange": [30, 75],\n  "escalationRequired": ["HIGH_VALUE", "PEP"]\n}',
      output: '{\n  "decision": "string",\n  "confidence": 0.0,\n  "reasoning": "string"\n}'
    },
    ENRICH: {
      context: '{\n  "enrichmentSources": ["INTERNAL_DB", "EXTERNAL_API"],\n  "maxLookups": 5,\n  "cacheEnabled": true\n}',
      output: '{\n  "enrichedPayload": {},\n  "fieldsAdded": 0,\n  "sources": []\n}'
    },
    ASSESS: {
      context: '{\n  "riskThresholds": {"low": 30, "medium": 60, "high": 80},\n  "highValueAmount": 25000,\n  "blockedCountries": ["NK", "IR", "SY"]\n}',
      output: '{\n  "riskScore": 0,\n  "riskLevel": "LOW",\n  "flags": [],\n  "recommendation": "string"\n}'
    },
    TRANSFORM: {
      context: '{\n  "targetFormat": "JSON",\n  "preserveOriginal": true,\n  "fieldMapping": {}\n}',
      output: '{\n  "transformed": {},\n  "fieldsChanged": 0\n}'
    }
  };

  const handleTaskChange = (newTask: AgentTaskType) => {
    setTask(newTask);
    const tmpl = TASK_TEMPLATES[newTask];
    if (tmpl) {
      // Only auto-fill if current values are default/empty
      if (!context || context === "{}" || context.includes("approvalThreshold") || context.includes("riskThresholds") || context.includes("categories") || context.includes("requiredFields") || context.includes("minAge") || context.includes("enrichmentSources") || context.includes("targetFormat")) {
        setContext(tmpl.context);
      }
      if (!outputSchema || outputSchema.includes('"decision"') || outputSchema.includes('"classification"') || outputSchema.includes('"valid"') || outputSchema.includes('"riskScore"') || outputSchema.includes('"enrichedPayload"') || outputSchema.includes('"transformed"') || outputSchema.includes('"extractedFields"')) {
        setOutputSchema(tmpl.output);
      }
    }
  };

  // ── Tools (selected from existing CIP services) ──
  const [cipServices, setCipServices] = useState<CipServiceRecord[]>([]);
  const [selectedTools, setSelectedTools] = useState<AgentTool[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [serviceFilter, setServiceFilter] = useState("");

  // ── Events (for ingress/egress selection) ──
  const [cipEvents, setCipEvents] = useState<CipEventRecord[]>([]);
  const [ingressEvent, setIngressEvent] = useState("");
  const [egressEvent, setEgressEvent] = useState("");
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [eventFilter, setEventFilter] = useState("");

  // ── Egress schema + JOLT mapping ──
  const [egressSchema, setEgressSchema] = useState<EgressSchemaInfo | null>(null);
  const [egressSchemaFields, setEgressSchemaFields] = useState<AvroField[]>([]);
  const [egressJolt, setEgressJolt] = useState("");
  const [joltMappings, setJoltMappings] = useState<JoltMappings>({});

  // ── Ingress schema ──
  const [ingressSchema, setIngressSchema] = useState<EgressSchemaInfo | null>(null);
  const [ingressSchemaFields, setIngressSchemaFields] = useState<AvroField[]>([]);

  // ── JOLT-transformed test output ──
  const [transformedOutput, setTransformedOutput] = useState<Record<string, unknown> | null>(null);

  // ── KB upload tracking ──
  const [kbUploads, setKbUploads] = useState<KbUploadEntry[]>([]);

  // ── Registered agents ──
  const [registeredAgents, setRegisteredAgents] = useState<Record<string, RegisteredAgentSummary>>({});

  // ── Test panel ──
  const [testPayload, setTestPayload] = useState('{\n  "customerId": "C-12345",\n  "amount": 50000,\n  "merchantName": "Test Corp"\n}');
  const [testResult, setTestResult] = useState<AgentTestResult | null>(null);
  const [testing, setTesting] = useState(false);

  // ── Status ──
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState<"studio" | "docs">("studio");
  const [wizardStep, setWizardStep] = useState(1);

  // ── Load existing CIP services for tool dropdown ──
  const loadServices = useCallback(async (keyword = "") => {
    if (!authToken) return;
    setLoadingServices(true);
    try {
      const token = await getToken();
      let allServices: CipServiceRecord[] = [];
      let page = 0;
      let totalPages = 1;

      while (page < totalPages && page < 5) { // max 5 pages = 500 services
        const searchBody: { status: string[]; services: Array<{ serviceName: string }>; exactNameMatch: boolean } = {
          status: ["ACTIVE"], services: [], exactNameMatch: false
        };
        if (keyword) searchBody.services = [{ serviceName: keyword }];

        const res = await fetch(`${CP_API_BASE}/services/search?page=${page}&size=100`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(searchBody)
        });
        if (res.ok) {
          const data = await res.json();
          allServices = [...allServices, ...(data.services || [])];
          totalPages = data.numberOfPages || 1;
          page++;
        } else break;
      }

      setCipServices(allServices);
      setStatus(`Loaded ${allServices.length} active services from CIP`);
    } catch (e) {
      setStatus("Failed to load services: " + getErrorMessage(e));
    }
    setLoadingServices(false);
  }, [authToken, CP_API_BASE, getToken]);

  // ── Load registered agents from agent runtime ──
  const loadAgents = useCallback(async () => {
    try {
      const res = await fetch(AGENT_CONFIGURE_URL);
      if (res.ok) setRegisteredAgents((await res.json()) as Record<string, RegisteredAgentSummary>);
    } catch { /* agent runtime may not be running */ }
  }, [AGENT_CONFIGURE_URL]);

  // ── Load existing CIP events for ingress/egress dropdown ──
  const loadEvents = useCallback(async (keyword = "") => {
    if (!authToken) return;
    setLoadingEvents(true);
    try {
      const token = await getToken();
      let allEvents: CipEventRecord[] = [];
      let page = 0;
      let totalPages = 1;

      while (page < totalPages && page < 5) {
        const searchBody: { status: string[]; events: Array<{ eventName: string }>; exactNameMatch: boolean } = {
          status: ["ACTIVE"], events: [], exactNameMatch: false
        };
        if (keyword) searchBody.events = [{ eventName: keyword }];

        const res = await fetch(`${CP_API_BASE}/events/search?page=${page}&size=100`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(searchBody)
        });
        if (res.ok) {
          const data = await res.json();
          allEvents = [...allEvents, ...(data.events || [])];
          totalPages = data.numberOfPages || 1;
          page++;
        } else break;
      }

      setCipEvents(allEvents);
    } catch (e) {
      console.error("Failed to load events:", e);
    }
    setLoadingEvents(false);
  }, [authToken, CP_API_BASE, getToken]);

  useEffect(() => {
    if (authToken) { loadServices(); loadAgents(); loadEvents(); }
  }, [authToken, loadServices, loadAgents, loadEvents]);

  // ── Client-side filtered results ──
  const filteredServices = cipServices.filter(s =>
    !serviceFilter || s.serviceName.toLowerCase().includes(serviceFilter.toLowerCase())
    || (s.serviceDescription || "").toLowerCase().includes(serviceFilter.toLowerCase())
  );

  const filteredEventsIngress = cipEvents.filter(ev =>
    !eventFilter || ev.eventName.toLowerCase().includes(eventFilter.toLowerCase())
    || (ev.eventDescription || "").toLowerCase().includes(eventFilter.toLowerCase())
  );

  const filteredEventsEgress = filteredEventsIngress; // same filter for both

  // ── Build sample payload from parsed Avro fields ──
  const buildSamplePayload = (parsedFields: AvroField[]): Record<string, unknown> => {
    const sample: Record<string, unknown> = {};
    for (const field of parsedFields) {
      // Skip parent record fields — only leaf fields
      if (field.subFields) continue;

      const parts = field.name.split(".");
      let obj: Record<string, unknown> = sample;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!obj[parts[i]]) obj[parts[i]] = {};
        obj = obj[parts[i]] as Record<string, unknown>;
      }
      const leafName = parts[parts.length - 1];
      const t = field.type;
      if (t.includes("string")) obj[leafName] = `sample-${leafName}`;
      else if (t.includes("int") || t.includes("long")) obj[leafName] = 0;
      else if (t.includes("double") || t.includes("float")) obj[leafName] = 0.0;
      else if (t.includes("boolean")) obj[leafName] = false;
      else if (t.includes("map")) obj[leafName] = {};
      else if (t.includes("array")) obj[leafName] = [];
      else obj[leafName] = null;
    }
    return sample;
  };

  // ── Get leaf field names (e.g. "payload.customerId" → "customerId") for JOLT mapping ──
  const getLeafFields = (parsedFields: AvroField[]) => {
    return parsedFields.filter(f => !f.subFields).map(f => f.name);
  };

  // ── Fetch egress event schema (just the schema, no JOLT yet) ──
  const fetchEgressSchema = useCallback(async (eventName: string) => {
    if (!eventName || !authToken) {
      setEgressSchema(null);
      setEgressSchemaFields([]);
      return;
    }

    try {
      const token = await getToken();
      const evObj = cipEvents.find(e => e.eventName === eventName);
      const version = evObj ? evObj.version : "1";

      const res = await fetch(`${CP_API_BASE}/events/${eventName}/version/${version}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) { setStatus("Failed to fetch event schema"); return; }

      const eventData = await res.json();
      const event = Array.isArray(eventData) ? eventData[0] : eventData;
      const schemaStr = event?.schema;

      if (!schemaStr) { setStatus("Event has no schema"); return; }

      const schema = JSON.parse(schemaStr) as AvroSchemaRecord;
      setEgressSchema(schema);
      setEgressSchemaFields(parseAvroFields(schema));
    } catch (e) {
      setStatus("Schema fetch error: " + getErrorMessage(e));
    }
  }, [authToken, cipEvents, CP_API_BASE, getToken]);

  // ── Auto-generate JOLT when ingress, egress, or output schema changes ──
  useEffect(() => {
    if (egressSchemaFields.length === 0) {
      setEgressJolt("");
      setJoltMappings({});
      return;
    }

    const egressLeafFields = egressSchemaFields.filter(f => !f.subFields).map(f => f.name);

    // Ingress leaf fields for pass-through matching
    const ingressLeaves = ingressSchemaFields
      .filter(f => !f.subFields)
      .map(f => ({ full: f.name, leaf: f.name.split(".").pop() }));

    // Agent output fields
    let parsedOutput = {};
    try { parsedOutput = JSON.parse(outputSchema); } catch { /* ignore */ }
    const agentFields = Object.keys(parsedOutput);

    console.log("[JOLT] ═══════════════════════════════════════");
    console.log("[JOLT] Ingress leaves (" + ingressLeaves.length + "):", JSON.stringify(ingressLeaves));
    console.log("[JOLT] Egress leaves (" + egressLeafFields.length + "):", JSON.stringify(egressLeafFields));
    console.log("[JOLT] Agent fields (" + agentFields.length + "):", JSON.stringify(agentFields));

    const shiftSpec: JoltMappings = {};
    const unmapped: string[] = [];

    for (const egressField of egressLeafFields) {
      const egressLeaf = egressField.split(".").pop();
      let mapped = false;

      // Priority 1: ingress field pass-through (same leaf name)
      const ingressMatch = ingressLeaves.find(i =>
        i.leaf === egressLeaf || (i.leaf ?? "").toLowerCase() === (egressLeaf ?? "").toLowerCase()
      );
      if (ingressMatch) {
        shiftSpec[ingressMatch.full] = egressField;
        mapped = true;
        console.log("[JOLT] ✓ INGRESS MATCH: " + ingressMatch.full + " → " + egressField);
      }

      // Priority 2: agent output field
      if (!mapped) {
        const agentMatch = agentFields.find(af =>
          af === egressLeaf || af.toLowerCase() === (egressLeaf ?? "").toLowerCase()
        );
        if (agentMatch) {
          shiftSpec[agentMatch] = egressField;
          mapped = true;
          console.log("[JOLT] ✓ AGENT MATCH: " + agentMatch + " → " + egressField);
        }
      }

      if (!mapped) {
        unmapped.push(egressField);
        console.log("[JOLT] ✗ UNMAPPED: " + egressField + " (leaf: " + egressLeaf + ")");
      }
    }

    // Agent fields not mapped to egress → store under _agentDecision
    for (const af of agentFields) {
      if (!Object.keys(shiftSpec).includes(af)) {
        shiftSpec[af] = "_agentDecision." + af;
      }
    }

    setJoltMappings(shiftSpec);

    // Build JOLT spec
    // Since the backend MERGES original payload + agent decision,
    // use pass-through ("@":"@") as base, then overlay specific mappings
    const joltSpec = [];

    // Step 1: Pass-through everything from the merged payload
    joltSpec.push({
      operation: "shift",
      spec: { "@": "@" }
    });

    // Step 2: Overlay specific agent decision mappings into _agentDecision
    const agentShift: Record<string, string> = {};
    for (const af of agentFields) {
      agentShift[af] = "_agentDecision." + af;
    }
    if (Object.keys(agentShift).length > 0) {
      joltSpec.push({
        operation: "shift",
        spec: {
          "_agentDecision": { "@": "_agentDecision" },
          "*": "&"
        }
      });
    }

    // Step 3: Remove internal fields the egress event doesn't need
    joltSpec.push({
      operation: "remove",
      spec: {
        "_agentNodeId": ""
      }
    });

    setEgressJolt(JSON.stringify(joltSpec, null, 2));

    const matchedIngress = Object.keys(shiftSpec).filter(k => !agentFields.includes(k) && !k.startsWith("_")).length;
    const matchedAgent = agentFields.length;
    setStatus(`✓ JOLT: pass-through all fields + ${matchedIngress} ingress matched, ${matchedAgent} agent fields, ${unmapped.length} egress-only fields (filled from merged payload)`);
  }, [ingressSchemaFields, egressSchemaFields, outputSchema]);

  // Trigger schema fetch when egress event changes
  const handleEgressChange = (eventName: string) => {
    setEgressEvent(eventName);
    fetchEgressSchema(eventName);
  };

  // Fetch ingress event schema and auto-populate test payload
  const handleIngressChange = async (eventName: string) => {
    setIngressEvent(eventName);
    if (!eventName || !authToken) {
      setIngressSchema(null);
      setIngressSchemaFields([]);
      return;
    }

    try {
      const token = await getToken();
      const evObj = cipEvents.find(e => e.eventName === eventName);
      const version = evObj ? evObj.version : "1";

      const res = await fetch(`${CP_API_BASE}/events/${eventName}/version/${version}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) return;

      const eventData = await res.json();
      const event = Array.isArray(eventData) ? eventData[0] : eventData;
      const schemaStr = event?.schema;

      if (!schemaStr) return;

      const schema = JSON.parse(schemaStr) as AvroSchemaRecord;
      setIngressSchema(schema);

      const parsedFields = parseAvroFields(schema);
      setIngressSchemaFields(parsedFields);

      const sample = buildSamplePayload(parsedFields);
      setTestPayload(JSON.stringify(sample, null, 2));
      setStatus(`✓ Test payload from ${eventName} (${parsedFields.filter(f => !f.subFields).length} fields)`);
    } catch (e) {
      console.error("Failed to fetch ingress schema:", e);
    }
  };

  // ── Add a service as a tool ──
  const addTool = (service: CipServiceRecord) => {
    if (!service) return;
    // Check if already added
    if (selectedTools.find(t => t.serviceName === service.serviceName)) {
      setStatus(`${service.serviceName} already added`);
      return;
    }

    const op = service.serviceOperations?.[0];
    const tool: AgentTool = {
      serviceName: service.serviceName,
      name: service.serviceName.replace(/[^a-zA-Z0-9]/g, "_"),
      description: service.serviceDescription || `Call ${service.serviceName}`,
      url: `${service.endPoint}${op?.operationUrl || "/"}`,
      method: op?.operationVerb || "POST",
      authType: service.authDetails?.authType === "ApiKey" ? "API_KEY" : "BEARER",
      authValue: "",
      inputSchema: buildInputSchema(op),
      outputSchema: {},
      timeoutMs: op?.timeout || 30000,
      // Keep original service ref for CIX registration
      _serviceRef: { serviceName: service.serviceName, version: service.version || 1, operationName: op?.serviceOperation }
    };
    setSelectedTools(prev => [...prev, tool]);
    setStatus(`Added ${service.serviceName} as tool`);
  };

  const removeTool = (name: string) => {
    setSelectedTools(prev => prev.filter(t => t.name !== name));
  };

  const updateTool = (idx: number, field: keyof AgentTool, value: unknown) => {
    setSelectedTools(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  // ── Build input schema from service operation's ingress events ──
  const buildInputSchema = (op?: CipServiceOperation) => {
    if (!op?.ingressEvents?.[0]) return {};
    const ie = op.ingressEvents[0];
    return { _note: `Ingress: ${ie.ingressEventName} v${ie.ingressEventVersion}` };
  };

  // ── Save agent: register in agent runtime ──
  const saveAgent = async () => {
    if (!agentName.trim()) { setStatus("Agent name is required"); return; }
    if (!instructions.trim()) { setStatus("Instructions are required"); return; }

    setSaving(true);
    setStatus("Saving agent...");

    const nodeId = agentName.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

    // Parse schemas
    let parsedOutput: Record<string, unknown> = {}, parsedContext: Record<string, unknown> = {};
    try { parsedOutput = JSON.parse(outputSchema); } catch { setStatus("Invalid output schema JSON"); setSaving(false); return; }
    try { parsedContext = JSON.parse(context); } catch { setStatus("Invalid context JSON"); setSaving(false); return; }

    // Build agent config
    const agentConfig = {
      nodeId,
      task,
      instructions: instructions.trim(),
      tools: selectedTools.map(t => {
        const base: Record<string, unknown> = {
          name: t.name,
          type: t.type || "REST",
          description: t.description,
          inputSchema: typeof t.inputSchema === "string" ? JSON.parse(t.inputSchema || "{}") : (t.inputSchema || {}),
          outputSchema: typeof t.outputSchema === "string" ? JSON.parse(t.outputSchema || "{}") : (t.outputSchema || {}),
        };
        // REST fields
        if (!t.type || t.type === "REST") {
          base.url = t.url; base.method = t.method; base.authType = t.authType;
          base.authValue = t.authValue; base.timeoutMs = t.timeoutMs || 10000; base.headers = {};
        }
        // DB_LOOKUP fields
        if (t.type === "DB_LOOKUP") {
          base.datasource = t.datasource || "json"; base.query = t.query || "";
          base.maxRows = t.maxRows || 100;
          let jd = t.jsonData;
          if (typeof jd === "string") { try { jd = JSON.parse(jd); } catch { jd = []; } }
          base.jsonData = jd || [];
          // JDBC config
          if (t.datasource === "jdbc") {
            base.jdbcName = t.jdbcName || ""; base.jdbcDriver = t.jdbcDriver || "postgresql";
            base.jdbcUrl = t.jdbcUrl || ""; base.jdbcUser = t.jdbcUser || ""; base.jdbcPassword = t.jdbcPassword || "";
          }
        }
        // RULES_ENGINE fields
        if (t.type === "RULES_ENGINE") {
          base.rulesMode = t.rulesMode || "LOCAL";
          base.rulesEndpoint = t.rulesEndpoint || "";
          let rules = t.rules;
          if (typeof rules === "string") { try { rules = JSON.parse(rules); } catch { rules = []; } }
          base.rules = rules || [];
          if (base.rulesMode === "EXTERNAL") { base.authType = t.authType; base.authValue = t.authValue; base.timeoutMs = t.timeoutMs || 10000; }
        }
        // KNOWLEDGE_BASE fields
        if (t.type === "KNOWLEDGE_BASE") {
          base.kbMode = t.kbMode || "INLINE"; base.collection = t.collection || "default";
          base.kbEndpoint = t.kbEndpoint || ""; base.topK = t.topK || 5;
          base.documents = Array.isArray(t.documents) ? t.documents : (typeof t.documents === "string" ? t.documents.split("\n===\n") : []);
          if (base.kbMode === "EXTERNAL") { base.authType = t.authType; base.authValue = t.authValue; base.timeoutMs = t.timeoutMs || 10000; }
        }
        return base;
      }),
      outputSchema: parsedOutput,
      context: parsedContext,
      maxIterations: maxIter,
      failOpen
    };

    try {
      // Register in agent runtime
      const agentRes = await fetch(AGENT_CONFIGURE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(agentConfig)
      });

      if (!agentRes.ok) {
        const err = await agentRes.json();
        setStatus("Agent registration failed: " + (err.error || agentRes.statusText));
        setSaving(false);
        return false;
      }

      const agentResult = await agentRes.json();

      // Step 2: Register in CIX (creates service wired to selected events)
      let cixStatus = "";
      try {
        const token = await getToken();

        // Find event versions from loaded data
        const ingressEvObj = cipEvents.find(e => e.eventName === ingressEvent);
        const egressEvObj = cipEvents.find(e => e.eventName === egressEvent);

        const cixRes = await fetch(`${AGENT_API_BASE}/agent/register-agent`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            nodeId,
            description: `AI Agent: ${agentName} — ${instructions.substring(0, 100)}`,
            endPoint: AGENT_PUBLIC_BASE,
            ingressEvent: ingressEvent || "",
            ingressEventVersion: ingressEvObj ? String(ingressEvObj.version) : "1",
            egressEvent: egressEvent || "",
            egressEventVersion: egressEvObj ? String(egressEvObj.version) : "1",
            egressJolt: egressJolt || ""
          })
        });

        if (cixRes.ok || cixRes.status === 201) {
          const cixData = await cixRes.json();
          cixStatus = ` | CIX: ${cixData.journeyUsage?.flow || "created"}`;
        } else {
          const cixErr = await cixRes.json().catch(() => ({}));
          cixStatus = " | CIX: " + (cixErr.error || cixRes.statusText);
        }
      } catch (e) {
        cixStatus = " | CIX skipped: " + getErrorMessage(e);
      }

      setStatus(`✓ Agent saved. Endpoint: POST /api/agent/${nodeId}${cixStatus}`);
      loadAgents();
      setSaving(false);
      return true;
    } catch (e) {
      setStatus("Error: " + getErrorMessage(e));
      setSaving(false);
      return false;
    }
  };

  // ── Test agent (auto-saves first) ──
  const testAgent = async () => {
    if (!agentName.trim()) { setStatus("Agent name is required"); return; }
    if (!instructions.trim()) { setStatus("Instructions are required"); return; }

    const nodeId = agentName.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

    // Auto-save before testing
    setStatus("Saving agent before test...");
    const saved = await saveAgent();
    if (saved === false) return;

    setTesting(true);
    setTestResult(null);
    setStatus("Running agent test...");

    try {
      let payload;
      try { payload = JSON.parse(testPayload); } catch { setStatus("Invalid test payload JSON"); setTesting(false); return; }

      const res = await fetch(`${AGENT_API_BASE}/agent/${nodeId}/debug`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const data = (await res.json()) as AgentTestResult;
      setTestResult(data);
      setStatus(data.success ? `✓ Test passed in ${data.executionTimeMs}ms` : `✗ Test failed: ${data.error}`);
    } catch (e) {
      setStatus("Test error: " + getErrorMessage(e));
    }
    setTesting(false);
  };

  // ── Load a registered agent into the editor ──
  const loadAgent = async (opId: string) => {
    try {
      const res = await fetch(`${AGENT_CONFIGURE_URL}/${opId}`);
      if (res.ok) {
        const cfg = (await res.json()) as RegisteredAgentConfig;
        setAgentName(cfg.nodeId || "");
        setTask((cfg.task as AgentTaskType) || "DECIDE");
        setInstructions(cfg.instructions || "");
        setOutputSchema(JSON.stringify(cfg.outputSchema || {}, null, 2));
        setContext(JSON.stringify(cfg.context || {}, null, 2));
        setMaxIter(cfg.maxIterations || 5);
        setFailOpen(cfg.failOpen || false);
        setSelectedTools((cfg.tools || []).map((t: AgentTool) => ({
          ...t, serviceName: t.name, _serviceRef: null
        })));
        setStatus(`Loaded agent: ${opId}`);
      }
    } catch (e) { setStatus("Failed to load: " + getErrorMessage(e)); }
  };

  // ── Rebuild JOLT JSON from visual mapping editor state ──
  const rebuildJoltFromMappings = (mappings: JoltMappings) => {
    const shiftSpec: JoltMappings = {};
    for (const [source, target] of Object.entries(mappings)) {
      shiftSpec[source] = target;
    }

    const joltSpec = [];
    // Pass-through base — all merged payload fields flow through
    joltSpec.push({ operation: "shift", spec: { "@": "@" } });
    // Specific field mappings (overrides from visual editor)
    if (Object.keys(shiftSpec).length > 0) {
      joltSpec.push({ operation: "shift", spec: { ...shiftSpec, "*": "&" } });
    }
    // Clean up
    joltSpec.push({ operation: "remove", spec: { "_agentNodeId": "" } });
    setEgressJolt(JSON.stringify(joltSpec, null, 2));
  };

  // ── Client-side JOLT simulator (handles nested input/output paths) ──
  const applySimpleJolt = (input: Record<string, unknown>, joltSpecStr: string): Record<string, unknown> => {
    try {
      const spec = JSON.parse(joltSpecStr) as Array<{ operation: string; spec?: Record<string, unknown> }>;
      const result: Record<string, unknown> = {};

      const getNested = (obj: Record<string, unknown>, path: string): unknown => {
        const parts = path.split(".");
        let current: unknown = obj;
        for (const part of parts) {
          if (current == null || typeof current !== "object") return undefined;
          current = (current as Record<string, unknown>)[part];
        }
        return current;
      };

      const setNested = (obj: Record<string, unknown>, path: string, value: unknown) => {
        const parts = path.split(".");
        let current = obj;
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) current[parts[i]] = {};
          current = current[parts[i]] as Record<string, unknown>;
        }
        current[parts[parts.length - 1]] = value;
      };

      for (const op of spec) {
        if (op.operation === "shift" && op.spec) {
          for (const [from, to] of Object.entries(op.spec)) {
            // Try nested read first (e.g. "payload.customerId" from merged payload)
            let value = getNested(input, from);
            if (value === undefined && Object.prototype.hasOwnProperty.call(input, from)) {
              value = input[from];
            }
            if (value !== undefined && typeof to === "string") {
              setNested(result, to, value);
            }
          }
        } else if (op.operation === "default" && op.spec) {
          for (const [key, val] of Object.entries(op.spec)) {
            if (getNested(result, key) === undefined) {
              setNested(result, key, val);
            }
          }
        }
      }

      return result;
    } catch (e) {
      return { _error: "JOLT parse error: " + getErrorMessage(e), _raw: input };
    }
  };

  const renderKbBuiltinUpload = (idx: number, tool: AgentTool) => (
    <div style={{ marginTop: 6 }}>
      <div style={{ ...S.card, borderColor: "#c8e6c9", background: "#f8fdf8" }}>
        <div style={{ ...S.label, fontSize: 10, color: "#2e7d32" }}>Upload Documents to Collection: <b>{tool.collection || "default"}</b></div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
          <input type="file" accept=".txt,.md,.csv,.json" multiple style={{ fontSize: 10 }}
            onChange={async (e) => {
              const files = Array.from(e.target.files ?? []);
              for (const file of files) {
                if (file.size > 5 * 1024 * 1024) { setStatus(`✗ ${file.name} exceeds 5MB limit`); continue; }
                try {
                  const text = await file.text();
                  const res = await fetch(`${AGENT_API_BASE}/knowledge/upload`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ collection: tool.collection || "default", text, source: file.name })
                  });
                  if (res.ok) {
                    const data = await res.json();
                    setKbUploads(prev => [...prev, { source: file.name, chunks: data.chunksCreated, collection: data.collection, size: (file.size / 1024).toFixed(1) + "KB", time: new Date().toLocaleTimeString() }]);
                    setStatus(`✓ Uploaded ${file.name}: ${data.chunksCreated} chunks`);
                  } else { setStatus(`✗ Upload failed: ${file.name}`); }
                } catch (err) { setStatus("Upload error: " + getErrorMessage(err)); }
              }
              e.target.value = "";
            }} />
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
          <textarea style={{ ...S.textarea, fontSize: 11, fontFamily: "monospace", minHeight: 50, padding: "6px 10px", flex: 1 }}
            placeholder="Paste document text here..." id={"kb-paste-" + idx} />
          <button style={{ ...S.btn, ...S.btnSuccess, fontSize: 10, padding: "6px 10px", whiteSpace: "nowrap" }}
            onClick={async () => {
              const ta = document.getElementById("kb-paste-" + idx) as HTMLTextAreaElement | null;
              const text = ta?.value;
              if (!text || text.trim().length < 20) { setStatus("Paste at least 20 characters"); return; }
              try {
                const res = await fetch(`${AGENT_API_BASE}/knowledge/upload`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ collection: tool.collection || "default", text, source: "pasted-text" })
                });
                if (res.ok) {
                  const data = await res.json();
                  setKbUploads(prev => [...prev, { source: "pasted-text", chunks: data.chunksCreated, collection: data.collection, size: (text.length / 1024).toFixed(1) + "KB", time: new Date().toLocaleTimeString() }]);
                  setStatus(`✓ Uploaded: ${data.chunksCreated} chunks to "${data.collection}"`);
                  if (ta) ta.value = "";
                }
              } catch (err) { setStatus("Upload error: " + getErrorMessage(err)); }
            }}>
            Upload
          </button>
        </div>
        {kbUploads.filter(u => u.collection === (tool.collection || "default")).length > 0 ? (
          <div style={{ marginTop: 10, border: "1px solid #e0e0e0", borderRadius: 6, overflow: "hidden" }}>
            <div style={{ background: "#e8f5e9", padding: "6px 10px", fontSize: 10, fontWeight: 600, color: "#2e7d32" }}>
              Uploaded ({kbUploads.filter(u => u.collection === (tool.collection || "default")).length} files)
            </div>
            {kbUploads.filter(u => u.collection === (tool.collection || "default")).map((upload, ui) => (
              <div key={ui} style={{ display: "flex", justifyContent: "space-between", padding: "4px 10px", fontSize: 11, borderTop: "1px solid #f0f0f0" }}>
                <span>{upload.source}</span>
                <span style={{ color: "#888" }}>{upload.chunks} chunks · {upload.size}</span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ marginTop: 8, padding: 8, textAlign: "center", fontSize: 10, color: "#aaa", border: "1px dashed #ddd", borderRadius: 6 }}>
            No documents uploaded yet.
          </div>
        )}
        <div style={{ marginTop: 6, fontSize: 9, color: "#999" }}>5 MB/file · .txt .md .csv .json</div>
      </div>
    </div>
  );

  const typeColors: Record<AgentToolType, string> = { REST: "#1565c0", DB_LOOKUP: "#e65100", RULES_ENGINE: "#7b1fa2", KNOWLEDGE_BASE: "#2e7d32" };
  const typeIcons: Record<AgentToolType, string> = { REST: "🔧", DB_LOOKUP: "🗄️", RULES_ENGINE: "⚖️", KNOWLEDGE_BASE: "📚" };

  return (
    <div style={S.app}>
      {/* ── Nav Bar ── */}
      <div style={S.navBar}>
        <span
          role="button"
          tabIndex={0}
          style={currentTab === "studio" ? S.navItemActive : S.navItem}
          onClick={() => setCurrentTab("studio")}
          onKeyDown={activateOnKey(() => setCurrentTab("studio"))}
        >
          Agent Studio
        </span>
        <span
          role="button"
          tabIndex={0}
          style={currentTab === "docs" ? S.navItemActive : S.navItem}
          onClick={() => setCurrentTab("docs")}
          onKeyDown={activateOnKey(() => setCurrentTab("docs"))}
        >
          Documentation
        </span>
      </div>

      {/* ── Documentation Panel ── */}
      {currentTab === "docs" && (
        <div style={{ display: "flex", height: "calc(100vh - 120px)" }}>
          {/* Left nav */}
          <div style={{ width: 240, background: "#fff", borderRight: "1px solid #e0e0e0", overflowY: "auto", flexShrink: 0, padding: "16px 0" }}>
            <div style={{ padding: "0 16px", marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1b2a4a", marginBottom: 12 }}>Documentation</div>
            </div>
            {[
              ["overview", "Overview"],
              ["step1", "Step 1: Name"],
              ["step2", "Step 2: Task Type"],
              ["step3", "Step 3: Instructions"],
              ["step4", "Step 4: Tools"],
              ["tool-rest", "  → REST API"],
              ["tool-db", "  → DB Lookup"],
              ["tool-rules", "  → Rules Engine"],
              ["tool-kb", "  → Knowledge Base"],
              ["step5", "Step 5: Output & Context"],
              ["step6", "Step 6: Events"],
              ["step7", "Step 7: JOLT Mapping"],
              ["step8", "Step 8: Test"],
              ["step9", "Step 9: Save"],
              ["journey", "Using in a Journey"],
              ["runtime", "Runtime Flow"],
              ["best", "Best Practices"],
            ].map(([id, label]) => (
              <div
                key={id}
                role="button"
                tabIndex={0}
                style={{ padding: "6px 16px", fontSize: 12, color: label.startsWith("  ") ? "#888" : "#333", cursor: "pointer", borderLeft: "3px solid transparent", fontWeight: label.startsWith("  ") ? 400 : 500 }}
                onClick={() => { const el = document.getElementById("doc-"+id); if(el) el.scrollIntoView({behavior:"smooth",block:"start"}); }}
                onKeyDown={activateOnKey(() => { const el = document.getElementById("doc-"+id); if(el) el.scrollIntoView({behavior:"smooth",block:"start"}); })}
                onMouseEnter={e => { const el = e.currentTarget; el.style.background="#f0f4ff"; el.style.borderLeftColor="#2e7d32"; }}
                onMouseLeave={e => { const el = e.currentTarget; el.style.background="transparent"; el.style.borderLeftColor="transparent"; }}>
                {label}
              </div>
            ))}
          </div>

          {/* Right content */}
          <div style={{ flex: 1, overflowY: "auto", padding: "24px 40px", background: "#fafbfc" }}>
            <h2 id="doc-overview" style={{ color: "#1b2a4a", borderBottom: "2px solid #2e7d32", paddingBottom: 8, marginBottom: 20 }}>AI Agent Studio — Documentation</h2>

            <div style={{ lineHeight: 1.8, fontSize: 14, color: "#333" }}>
              <p>An AI Agent is a <b>regular CIP service</b> powered by Claude AI. It sits in a journey DAG like any REST service but uses AI reasoning + tools (REST APIs, DB queries, rules, knowledge base) to make business decisions.</p>

              <div style={{ background: "#e8edf5", border: "1px solid #c5cae9", borderRadius: 8, padding: 16, margin: "16px 0", fontFamily: "monospace", fontSize: 12, lineHeight: 2 }}>
                <div style={{ textAlign: "center", fontWeight: 700, color: "#1b2a4a", marginBottom: 4 }}>Architecture</div>
                [Event] → <b style={{ color: "#1565c0" }}>[AI Agent]</b> → [Event] → [Service] → ...<br/>
                {"              "}├─ 🔧 REST API &nbsp;&nbsp;├─ 🗄️ DB Lookup<br/>
                {"              "}├─ ⚖️ Rules &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;└─ 📚 Knowledge Base
              </div>

              <h3 id="doc-step1" style={{ color: "#1b2a4a", marginTop: 32 }}>Step 1: Name Your Agent</h3>
              <p>Unique name → becomes operation ID, URL path, and CIP service name.</p>

              <h3 id="doc-step2" style={{ color: "#1b2a4a", marginTop: 32 }}>Step 2: Task Type</h3>
              <p>DECIDE, ASSESS, CLASSIFY, VALIDATE, EXTRACT, ENRICH, TRANSFORM — auto-populates output schema and context templates.</p>

              <h3 id="doc-step3" style={{ color: "#1b2a4a", marginTop: 32 }}>Step 3: Agent Instructions</h3>
              <p>Natural language prompt. Be specific: thresholds, criteria, when to use which tool.</p>

              <h3 id="doc-step4" style={{ color: "#1b2a4a", marginTop: 32 }}>Step 4: Configure Tools</h3>

              <div id="doc-tool-rest" style={{ background: "#e3f2fd", border: "1px solid #bbdefb", borderRadius: 8, padding: 16, margin: "12px 0", borderLeft: "4px solid #1565c0" }}>
                <h4 style={{ color: "#1565c0", margin: "0 0 6px" }}>🔧 REST API Call</h4>
                <p style={{ margin: 0 }}>Call CIP services or any HTTP endpoint. <code>BEARER</code> auth forwards the journey token. Select from the service dropdown or enter a custom URL.</p>
              </div>

              <div id="doc-tool-db" style={{ background: "#fff3e0", border: "1px solid #ffe0b2", borderRadius: 8, padding: 16, margin: "12px 0", borderLeft: "4px solid #e65100" }}>
                <h4 style={{ color: "#e65100", margin: "0 0 6px" }}>🗄️ DB Lookup</h4>
                <p style={{ margin: 0 }}><b>Two modes:</b></p>
                <p style={{ margin: "6px 0 0" }}><b>JDBC</b> — real database query with <code>:paramName</code> placeholders. Requires datasource in <code>application.yml</code>.</p>
                <p style={{ margin: "4px 0 0" }}><b>JSON Dataset</b> — paste a JSON array in the tool config. Agent queries it in-memory. No database needed — perfect for testing or small reference data (risk tiers, country codes, product catalogs).</p>
                <div style={{ background: "#fff", borderRadius: 4, padding: 8, fontFamily: "monospace", fontSize: 11, marginTop: 8, color: "#555" }}>
                  JSON example: [{`{"customerId":"C-001","name":"John","riskTier":"LOW"}`},{`{"customerId":"C-002","name":"Jane","riskTier":"HIGH"}`}]
                </div>
              </div>

              <div id="doc-tool-rules" style={{ background: "#f3e5f5", border: "1px solid #e1bee7", borderRadius: 8, padding: 16, margin: "12px 0", borderLeft: "4px solid #7b1fa2" }}>
                <h4 style={{ color: "#7b1fa2", margin: "0 0 6px" }}>⚖️ Rules Engine</h4>
                <p style={{ margin: 0 }}><b>Two modes:</b></p>
                <p style={{ margin: "6px 0 0" }}><b>LOCAL</b> — JSON rules evaluated deterministically in Java. Same input → same output. Auditable.</p>
                <p style={{ margin: "4px 0 0" }}><b>EXTERNAL</b> — calls an external rules service (Drools, DMN, OPA) via REST endpoint.</p>
                <p style={{ margin: "8px 0 4px" }}>Operators: <code>eq, neq, gt, gte, lt, lte, in, not_in, contains, between, exists, empty</code></p>
                <div style={{ background: "#fff", borderRadius: 4, padding: 8, fontFamily: "monospace", fontSize: 11, marginTop: 4, color: "#555" }}>
                  [{`{"name":"HIGH_VALUE","condition":{"field":"payload.amount","op":"gt","value":25000},"action":{"flag":"HIGH_VALUE"}}`}]
                </div>
                <p style={{ margin: "6px 0 0", fontSize: 12, color: "#888" }}>Unlike Business Context (AI-interpreted), rules execute as deterministic code.</p>
              </div>

              <div id="doc-tool-kb" style={{ background: "#e8f5e9", border: "1px solid #c8e6c9", borderRadius: 8, padding: 16, margin: "12px 0", borderLeft: "4px solid #2e7d32" }}>
                <h4 style={{ color: "#2e7d32", margin: "0 0 6px" }}>📚 Knowledge Base (RAG)</h4>
                <p style={{ margin: 0 }}><b>Three modes:</b></p>
                <p style={{ margin: "6px 0 0" }}><b>BUILTIN</b> — built-in RAG engine. Upload documents via <code>POST /api/agent/knowledge/upload</code>. BM25 keyword search. No external service needed.</p>
                <p style={{ margin: "4px 0 0" }}><b>EXTERNAL</b> — calls Azure AI Search, Bedrock KB, Pinecone, or any RAG API endpoint.</p>
                <p style={{ margin: "4px 0 0" }}><b>INLINE</b> — paste policy text directly in the tool config. Chunks and indexes automatically. Best for small document sets (&lt; 10 pages).</p>
                <p style={{ margin: "6px 0 0", fontSize: 12, color: "#888" }}>Agent asks: &quot;What is the policy on cross-border transactions?&quot; → gets relevant text → uses in reasoning.</p>
              </div>

              <h3 id="doc-step5" style={{ color: "#1b2a4a", marginTop: 32 }}>Step 5: Output Schema & Business Context</h3>
              <p><b>Output Schema</b> — JSON the agent returns. Drives JOLT. <b>Context</b> — static thresholds/rules in the AI prompt (AI-interpreted, not deterministic).</p>

              <h3 id="doc-step6" style={{ color: "#1b2a4a", marginTop: 32 }}>Step 6: Ingress & Egress Events</h3>
              <p>Select existing journey events for stitchability. Ingress populates test payload. Egress drives JOLT editor. Leave blank to auto-create.</p>

              <h3 id="doc-step7" style={{ color: "#1b2a4a", marginTop: 32 }}>Step 7: JOLT Mapping</h3>
              <p>Visual editor: each egress field gets a dropdown — <span style={{ background: "#e3f2fd", padding: "1px 6px", borderRadius: 6, fontSize: 11 }}>ingress</span> pass-through, <span style={{ background: "#e8f5e9", padding: "1px 6px", borderRadius: 6, fontSize: 11 }}>agent</span> decision, or <span style={{ background: "#f3e5f5", padding: "1px 6px", borderRadius: 6, fontSize: 11 }}>pass</span> from merged payload.</p>

              <h3 id="doc-step8" style={{ color: "#1b2a4a", marginTop: 32 }}>Step 8: Test & Step 9: Save</h3>
              <p><b>Test</b> auto-saves, runs agent, shows raw output + JOLT-transformed egress + reasoning trace. <b>Save</b> creates CIX events + service + approves to ACTIVE.</p>

              <h3 id="doc-journey" style={{ color: "#1b2a4a", marginTop: 32 }}>Using in a CIP Journey</h3>
              <div style={{ background: "#eef5ee", border: "1px solid #c8e6c9", borderRadius: 8, padding: 16, marginBottom: 12 }}>
                <ol style={{ margin: 0, paddingLeft: 20 }}>
                  <li>Journey Designer → add Step Node → select <code>ai-agent-{'{name}'}</code></li>
                  <li>Wire ingress/egress events → JOLT pre-configured → approve</li>
                </ol>
              </div>

              <h3 id="doc-runtime" style={{ color: "#1b2a4a", marginTop: 32 }}>Runtime — ReAct Loop</h3>
              <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 6, padding: 12, fontFamily: "monospace", fontSize: 12, lineHeight: 1.8 }}>
                RAER → Service Handler → POST /api/agent/{'{id}'}<br/>
                ┌─ Iter 1: THOUGHT → calls ⚖️ Rules → &quot;HIGH_VALUE fired&quot;<br/>
                ├─ Iter 2: THOUGHT → calls 🗄️ DB → &quot;credit_score: 720&quot;<br/>
                ├─ Iter 3: THOUGHT → calls 📚 KB → &quot;policy: approve if score {'>'} 700&quot;<br/>
                └─ FINAL ANSWER → merged with payload → JOLT → egress event → next node
              </div>

              <h3 id="doc-best" style={{ color: "#1b2a4a", marginTop: 32 }}>Best Practices</h3>
              <div style={{ background: "#f8f9fa", border: "1px solid #e0e0e0", borderRadius: 8, padding: 16 }}>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                  <li><b>Specific instructions</b> with thresholds and tool usage guidance</li>
                  <li><b>Clear tool descriptions</b> — AI uses these to decide when to call each tool</li>
                  <li><b>Rules Engine for compliance</b> — deterministic, auditable</li>
                  <li><b>JSON datasets</b> for testing DB lookups without a real database</li>
                  <li><b>Inline documents</b> for testing KB search without a vector store</li>
                  <li><b>Existing events</b> for DAG stitchability</li>
                  <li><b>Low max iterations</b> (3-5) to control latency</li>
                  <li><b>Review traces</b> to verify tool call order and reasoning</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {currentTab === "studio" && (
      <div style={S.main}>
        {/* ══════════════ LEFT PANEL — Wizard ══════════════ */}
        <div style={S.panel}>

          {/* Registered agents + Reload */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
              {Object.entries(registeredAgents).map(([id, info]) => (
                <button key={id} style={{ ...S.chip, cursor: "pointer", border: "1px solid #1b2a4a22", padding: "3px 8px", fontSize: 10 }}
                  onClick={() => { loadAgent(id); setWizardStep(1); }}>
                  <span style={{ ...S.tag, background: "#1b2a4a15", color: "#1b2a4a", fontSize: 9 }}>{info.task}</span>
                  {id}
                </button>
              ))}
              {Object.keys(registeredAgents).length === 0 && <span style={{ fontSize: 10, color: "#bbb" }}>No agents</span>}
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              <button style={{ ...S.btn, ...S.btnGhost, fontSize: 9, padding: "3px 8px" }}
                onClick={() => { setAgentName(""); setInstructions(""); setSelectedTools([]); setWizardStep(1); setTestResult(null); }}>
                + New
              </button>
<button style={{ ...S.btn, ...S.btnGhost, fontSize: 9, padding: "3px 8px" }}
                onClick={() => loadAgents()}>↻</button>
            </div>
          </div>

          {/* ── Step Indicator ── */}
          <div style={{ display: "flex", marginBottom: 16, borderBottom: "2px solid #e0e0e0" }}>
            {([
              { num: 1, label: "Basics", done: !!(agentName && instructions) },
              { num: 2, label: "Tools", done: selectedTools.length > 0 },
              { num: 3, label: "Schema", done: !!(outputSchema && outputSchema !== "{}") },
              { num: 4, label: "Events", done: !!(ingressEvent || egressEvent) },
              { num: 5, label: "Test", done: !!testResult?.success },
              { num: 6, label: "Save", done: false },
            ] as const).map(({ num, label, done }) => (
              <div key={num} role="button" tabIndex={0} onClick={() => setWizardStep(num)} onKeyDown={activateOnKey(() => setWizardStep(num))}
                style={{
                  flex: 1, padding: "10px 2px", textAlign: "center", cursor: "pointer",
                  borderBottom: wizardStep === num ? "3px solid #2e7d32" : "3px solid transparent",
                  marginBottom: -2, transition: "all 0.15s"
                }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3 }}>
                  <span style={{
                    width: 18, height: 18, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontSize: 9, fontWeight: 700,
                    background: done ? "#2e7d32" : wizardStep === num ? "#1b2a4a" : "#e0e0e0",
                    color: done || wizardStep === num ? "#fff" : "#888"
                  }}>{done ? "✓" : num}</span>
                  <span style={{ fontSize: 10, fontWeight: wizardStep === num ? 600 : 400, color: wizardStep === num ? "#1b2a4a" : "#888" }}>{label}</span>
                </div>
              </div>
            ))}
          </div>

          {/* ══════════════ STEP 1: Basics ══════════════ */}
          {wizardStep === 1 && (
            <div>
              <h3 style={{ color: "#1b2a4a", margin: "0 0 16px", fontSize: 15 }}>Agent Identity & Instructions</h3>

              <div style={S.section}>
                <label htmlFor="cip-agent-name" style={S.label}>Agent Name <span style={{ color: "#c62828" }}>*</span></label>
                <input id="cip-agent-name" style={{ ...S.input, borderColor: !agentName ? "#ffcdd2" : "#d0d0d0" }}
                  value={agentName} onChange={e => setAgentName(e.target.value)} placeholder="e.g. fraud-assessment" />
                {agentName && <div style={{ fontSize: 10, color: "#2e7d32", marginTop: 3 }}>
                  ✓ Endpoint: /api/agent/{agentName.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}
                </div>}
              </div>

              <div style={S.grid2}>
                <div style={S.section}>
                  <label htmlFor="cip-agent-task" style={S.label}>Task Type</label>
                  <select id="cip-agent-task" style={S.select} value={task} onChange={e => handleTaskChange(e.target.value as AgentTaskType)}>
                    {TASK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div style={S.section}>
                  <label htmlFor="cip-agent-max-iter" style={S.label}>Max Iterations</label>
                  <input id="cip-agent-max-iter" type="number" style={S.input} value={maxIter}
                    onChange={e => setMaxIter(parseInt(e.target.value) || 5)} min={1} max={10} />
                </div>
              </div>

              <div style={S.section}>
                <label htmlFor="cip-agent-instructions" style={S.label}>Agent Instructions <span style={{ color: "#c62828" }}>*</span></label>
                <textarea id="cip-agent-instructions" style={{ ...S.textarea, minHeight: 150, borderColor: !instructions ? "#ffcdd2" : "#d0d0d0" }}
                  value={instructions} onChange={e => setInstructions(e.target.value)}
                  placeholder="Describe what this agent should do. Be specific about business logic, thresholds, and when to use each tool..." />
              </div>

              <label style={{ ...S.label, marginBottom: 0, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="checkbox" checked={failOpen} onChange={e => setFailOpen(e.target.checked)} />
                Fail-open (pass through on failure)
              </label>
            </div>
          )}

          {/* ══════════════ STEP 2: Tools ══════════════ */}
          {wizardStep === 2 && (
            <div>
              <h3 style={{ color: "#1b2a4a", margin: "0 0 4px", fontSize: 15 }}>Configure Tools ({selectedTools.length})</h3>
              <p style={{ color: "#888", fontSize: 12, margin: "0 0 12px" }}>Add CIP services, database lookups, rules engines, or knowledge bases the agent can use.</p>

              {/* CIP service dropdown */}
              <div style={S.section}>
                <label style={S.label}>
                  Add CIP Service as REST Tool
                  <button style={{ ...S.btn, ...S.btnGhost, padding: "2px 8px", fontSize: 9, marginLeft: 6 }}
                    onClick={() => loadServices()} disabled={loadingServices}>{loadingServices ? "..." : "↻"}</button>
                  <span style={{ fontWeight: 400, color: "#aaa", marginLeft: 4 }}>{cipServices.length} loaded</span>
                </label>
                <div style={{ display: "flex", gap: 4 }}>
                  <input style={{ ...S.input, fontSize: 11, flex: 1 }} placeholder="🔍 Filter services..."
                    value={serviceFilter} onChange={e => setServiceFilter(e.target.value)} />
                </div>
                <select style={{ ...S.select, marginTop: 4, fontSize: 11 }}
                  onChange={e => { const svc = cipServices.find(s => s.serviceName === e.target.value); if (svc) addTool(svc); e.target.value = ""; }}
                  value="">
                  <option value="">+ Select a CIP service... ({filteredServices.length} shown)</option>
                  {filteredServices.map(s => (
                    <option key={s.serviceName + s.version} value={s.serviceName}>
                      {s.serviceName} — {s.serviceOperations?.[0]?.operationVerb} {s.serviceOperations?.[0]?.operationUrl || ""}
                    </option>
                  ))}
                </select>
              </div>

              {/* Add custom tool buttons */}
              <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                <button style={{ ...S.btn, ...S.btnGhost, fontSize: 10, padding: "5px 10px", borderColor: "#e65100", color: "#e65100" }}
                  onClick={() => { const n = selectedTools.filter(t=>t.type==="DB_LOOKUP").length+1; setSelectedTools(prev => [...prev, { name: "db_lookup_"+n, type: "DB_LOOKUP", description: "Query database for data", datasource: "json", query: "", maxRows: 100, jsonData: [{"customerId":"C-001","name":"John Doe","riskTier":"LOW","creditScore":750}] }]); }}>
                  🗄️ + DB Lookup
                </button>
                <button style={{ ...S.btn, ...S.btnGhost, fontSize: 10, padding: "5px 10px", borderColor: "#7b1fa2", color: "#7b1fa2" }}
                  onClick={() => { const n = selectedTools.filter(t=>t.type==="RULES_ENGINE").length+1; setSelectedTools(prev => [...prev, { name: "rules_engine_"+n, type: "RULES_ENGINE", rulesMode: "LOCAL", description: "Evaluate business rules", rules: [{"name":"HIGH_VALUE","condition":{"field":"payload.requestedAmount","op":"gt","value":25000},"action":{"flag":"HIGH_VALUE"}}] }]); }}>
                  ⚖️ + Rules Engine
                </button>
                <button style={{ ...S.btn, ...S.btnGhost, fontSize: 10, padding: "5px 10px", borderColor: "#2e7d32", color: "#2e7d32" }}
                  onClick={() => { const n = selectedTools.filter(t=>t.type==="KNOWLEDGE_BASE").length+1; setSelectedTools(prev => [...prev, { name: "knowledge_base_"+n, type: "KNOWLEDGE_BASE", kbMode: "INLINE", description: "Search policies and documents", collection: "policies", topK: 5, documents: ["Paste policy documents here."] }]); }}>
                  📚 + Knowledge Base
                </button>
              </div>

              {/* Tool cards */}
              <div style={{ maxHeight: "calc(100vh - 340px)", overflowY: "auto" }}>
                {selectedTools.length === 0 && (
                  <div style={{ ...S.empty, border: "1px dashed #ddd", borderRadius: 8 }}>No tools added yet. Add a CIP service or custom tool above.</div>
                )}
                {selectedTools.map((tool, idx) => {
                  const toolType: AgentToolType = tool.type || "REST";
                  return (
                  <details key={tool.name + idx} open={idx === selectedTools.length - 1} style={{ ...S.card, borderLeft: "3px solid " + (typeColors[toolType] || "#999"), padding: 0, overflow: "hidden", marginBottom: 6 }}>
                    <summary style={{ padding: "8px 12px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fafafa" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span>{typeIcons[toolType] || "🔧"}</span>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{tool.name}</span>
                        <span style={{ ...S.tag, background: (typeColors[toolType] || "#999") + "15", color: typeColors[toolType], fontSize: 9 }}>{toolType}</span>
                      </div>
                      <button style={{ ...S.chipRemove, fontSize: 11 }} onClick={e => { e.preventDefault(); removeTool(tool.name); }}>✕</button>
                    </summary>
                    <div style={{ padding: "8px 12px" }}>
                      <div style={S.grid2}>
                        <div>
                          <label htmlFor={`cip-tool-name-${idx}`} style={{ ...S.label, fontSize: 9 }}>Tool Name</label>
                          <input id={`cip-tool-name-${idx}`} style={{ ...S.input, fontSize: 11, padding: "5px 8px" }} value={tool.name}
                            onChange={e => updateTool(idx, "name", e.target.value)} />
                        </div>
                        <div>
                          <label htmlFor={`cip-tool-type-${idx}`} style={{ ...S.label, fontSize: 9 }}>Type</label>
                          <select id={`cip-tool-type-${idx}`} style={{ ...S.select, fontSize: 11, padding: "5px 8px" }} value={toolType}
                            onChange={e => {
                              const newType = e.target.value as AgentToolType;
                              const typePrefix: Record<AgentToolType, string> = { REST: "rest_api", DB_LOOKUP: "db_lookup", RULES_ENGINE: "rules_engine", KNOWLEDGE_BASE: "knowledge_base" };
                              const count = selectedTools.filter(t => (t.type || "REST") === newType).length + 1;
                              const descMap: Record<AgentToolType, string> = { REST: "Call business service", DB_LOOKUP: "Query database", RULES_ENGINE: "Evaluate rules", KNOWLEDGE_BASE: "Search documents" };
                              setSelectedTools(prev => { const u = [...prev]; u[idx] = { ...u[idx], type: newType, name: typePrefix[newType] + "_" + count, description: descMap[newType] || u[idx].description }; return u; });
                            }}>
                            <option value="REST">REST API</option><option value="DB_LOOKUP">DB Lookup</option>
                            <option value="RULES_ENGINE">Rules Engine</option><option value="KNOWLEDGE_BASE">Knowledge Base</option>
                          </select>
                        </div>
                      </div>
                      <div style={{ marginTop: 4 }}>
                        <label htmlFor={`cip-tool-desc-${idx}`} style={{ ...S.label, fontSize: 9 }}>Description (tells AI when to use this)</label>
                        <input id={`cip-tool-desc-${idx}`} style={{ ...S.input, fontSize: 11, padding: "5px 8px" }} value={tool.description}
                          onChange={e => updateTool(idx, "description", e.target.value)} />
                      </div>

                      {/* Type-specific fields — same as before but more compact */}
                      {toolType === "REST" && (
                        <div style={S.grid2}>
                          <div style={{ marginTop: 4 }}><label htmlFor={`cip-tool-auth-${idx}`} style={{ ...S.label, fontSize: 9 }}>Auth</label>
                            <select id={`cip-tool-auth-${idx}`} style={{ ...S.select, fontSize: 10, padding: "4px 6px" }} value={tool.authType || "NONE"} onChange={e => updateTool(idx, "authType", e.target.value)}>
                              <option value="NONE">None</option><option value="BEARER">Bearer</option><option value="API_KEY">API Key</option>
                            </select></div>
                          <div style={{ marginTop: 4 }}><label htmlFor={`cip-tool-method-${idx}`} style={{ ...S.label, fontSize: 9 }}>Method</label>
                            <select id={`cip-tool-method-${idx}`} style={{ ...S.select, fontSize: 10, padding: "4px 6px" }} value={tool.method || "POST"} onChange={e => updateTool(idx, "method", e.target.value)}>
                              <option value="POST">POST</option><option value="GET">GET</option><option value="PUT">PUT</option>
                            </select></div>
                        </div>
                      )}
                      {toolType === "DB_LOOKUP" && (
                        <div style={{ marginTop: 4 }}>
                          <select style={{ ...S.select, fontSize: 10, padding: "4px 6px", marginBottom: 4 }} value={tool.datasource || "json"} onChange={e => updateTool(idx, "datasource", e.target.value)}>
                            <option value="json">JSON Dataset (testing)</option><option value="jdbc">JDBC (production)</option>
                          </select>
                          {(!tool.datasource || tool.datasource === "json") && (
                            <textarea style={{ ...S.textarea, fontSize: 10, fontFamily: "monospace", minHeight: 50, padding: "4px 6px" }}
                              value={typeof tool.jsonData === "string" ? tool.jsonData : JSON.stringify(tool.jsonData || [], null, 2)}
                              onChange={e => updateTool(idx, "jsonData", e.target.value)} />
                          )}
                          {tool.datasource === "jdbc" && (
                            <div>
                              <input style={{ ...S.input, fontSize: 10, padding: "4px 6px", marginBottom: 3 }} value={tool.jdbcUrl || ""} placeholder="jdbc:postgresql://host:5432/db" onChange={e => updateTool(idx, "jdbcUrl", e.target.value)} />
                              <textarea style={{ ...S.textarea, fontSize: 10, fontFamily: "monospace", minHeight: 40, padding: "4px 6px" }} value={tool.query || ""} placeholder="SELECT * FROM t WHERE id = :customerId" onChange={e => updateTool(idx, "query", e.target.value)} />
                            </div>
                          )}
                        </div>
                      )}
                      {toolType === "RULES_ENGINE" && (
                        <div style={{ marginTop: 4 }}>
                          <select style={{ ...S.select, fontSize: 10, padding: "4px 6px", marginBottom: 4 }} value={tool.rulesMode || "LOCAL"} onChange={e => updateTool(idx, "rulesMode", e.target.value)}>
                            <option value="LOCAL">Local JSON Rules</option><option value="EXTERNAL">External Service</option>
                          </select>
                          {(tool.rulesMode || "LOCAL") === "LOCAL" && (
                            <textarea style={{ ...S.textarea, fontSize: 10, fontFamily: "monospace", minHeight: 60, padding: "4px 6px" }}
                              value={typeof tool.rules === "string" ? tool.rules : JSON.stringify(tool.rules || [], null, 2)}
                              onChange={e => updateTool(idx, "rules", e.target.value)} />
                          )}
                          {tool.rulesMode === "EXTERNAL" && (
                            <input style={{ ...S.input, fontSize: 10, padding: "4px 6px" }} value={tool.rulesEndpoint || ""} placeholder="https://rules-engine.bank.com/evaluate" onChange={e => updateTool(idx, "rulesEndpoint", e.target.value)} />
                          )}
                        </div>
                      )}
                      {toolType === "KNOWLEDGE_BASE" && (
                        <div style={{ marginTop: 4 }}>
                          <div style={S.grid2}>
                            <select style={{ ...S.select, fontSize: 10, padding: "4px 6px" }} value={tool.kbMode || "INLINE"} onChange={e => updateTool(idx, "kbMode", e.target.value)}>
                              <option value="INLINE">Inline Docs</option><option value="BUILTIN">Built-in RAG</option><option value="EXTERNAL">External</option>
                            </select>
                            <input style={{ ...S.input, fontSize: 10, padding: "4px 6px" }} value={tool.collection || ""} placeholder="Collection" onChange={e => updateTool(idx, "collection", e.target.value)} />
                          </div>
                          {(tool.kbMode || "INLINE") === "INLINE" && (
                            <textarea style={{ ...S.textarea, fontSize: 10, minHeight: 50, padding: "4px 6px", marginTop: 3 }}
                              value={Array.isArray(tool.documents) ? tool.documents.join("\n===\n") : (tool.documents || "")}
                              onChange={e => updateTool(idx, "documents", e.target.value.split("\n===\n"))} placeholder="Paste policy documents (separate with ===)" />
                          )}
                          {tool.kbMode === "EXTERNAL" && (
                            <input style={{ ...S.input, fontSize: 10, padding: "4px 6px", marginTop: 3 }} value={tool.kbEndpoint || ""} placeholder="RAG endpoint URL" onChange={e => updateTool(idx, "kbEndpoint", e.target.value)} />
                          )}
                          {tool.kbMode === "BUILTIN" && renderKbBuiltinUpload(idx, tool)}
                        </div>
                      )}
                    </div>
                  </details>
                  );
                })}
              </div>
            </div>
          )}

          {/* ══════════════ STEP 3: Schema & Context ══════════════ */}
          {wizardStep === 3 && (
            <div>
              <h3 style={{ color: "#1b2a4a", margin: "0 0 16px", fontSize: 15 }}>Output Schema & Business Context</h3>

              <div style={S.section}>
                <label htmlFor="cip-output-schema" style={S.label}>Output Schema (JSON) — what the agent returns</label>
                <textarea id="cip-output-schema" style={{ ...S.textarea, minHeight: 120, fontFamily: "monospace", fontSize: 12 }}
                  value={outputSchema} onChange={e => setOutputSchema(e.target.value)} />
                <div style={{ fontSize: 10, color: "#888", marginTop: 3 }}>This defines the structure of the agent&apos;s decision. The AI must return JSON matching this schema.</div>
              </div>

              <div style={S.section}>
                <label htmlFor="cip-context" style={S.label}>Business Context (thresholds, rules, policies)</label>
                <textarea id="cip-context" style={{ ...S.textarea, minHeight: 120, fontFamily: "monospace", fontSize: 12 }}
                  value={context} onChange={e => setContext(e.target.value)} />
                <div style={{ fontSize: 10, color: "#888", marginTop: 3 }}>Static data injected into the AI&apos;s prompt. Use for thresholds and guidelines the AI should consider (interpreted by AI, not deterministic — use Rules Engine for deterministic logic).</div>
              </div>
            </div>
          )}

          {/* ══════════════ STEP 4: Events & JOLT ══════════════ */}
          {wizardStep === 4 && (
            <div>
              <h3 style={{ color: "#1b2a4a", margin: "0 0 4px", fontSize: 15 }}>Journey Wiring</h3>
              <p style={{ color: "#888", fontSize: 12, margin: "0 0 12px" }}>Select existing CIP events to make the agent stitchable in journey DAGs.</p>

              <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
                <input style={{ ...S.input, fontSize: 11, flex: 1 }} placeholder="🔍 Filter events..."
                  value={eventFilter} onChange={e => setEventFilter(e.target.value)} />
                <button style={{ ...S.btn, ...S.btnGhost, fontSize: 9, padding: "4px 8px" }}
                  onClick={() => loadEvents()} disabled={loadingEvents}>{loadingEvents ? "..." : "↻"}</button>
                <span style={{ fontSize: 9, color: "#aaa", alignSelf: "center" }}>{cipEvents.length}</span>
              </div>

              <div style={S.grid2}>
                <div style={S.section}>
                  <label htmlFor="cip-ingress-event" style={{ ...S.label, color: "#2e7d32", fontSize: 10 }}>📥 Ingress Event (input)</label>
                  <select id="cip-ingress-event" style={S.select} value={ingressEvent} onChange={e => handleIngressChange(e.target.value)}>
                    <option value="">Select...</option>
                    {filteredEventsIngress.map(ev => <option key={ev.eventName + ev.version} value={ev.eventName}>{ev.eventName} (v{ev.version})</option>)}
                  </select>
                </div>
                <div style={S.section}>
                  <label htmlFor="cip-egress-event" style={{ ...S.label, color: "#c62828", fontSize: 10 }}>📤 Egress Event (output)</label>
                  <select id="cip-egress-event" style={S.select} value={egressEvent} onChange={e => handleEgressChange(e.target.value)}>
                    <option value="">Select...</option>
                    {filteredEventsEgress.map(ev => <option key={ev.eventName + ev.version} value={ev.eventName}>{ev.eventName} (v{ev.version})</option>)}
                  </select>
                </div>
              </div>

              {(!ingressEvent || !egressEvent) && (
                <div style={{ fontSize: 11, color: "#e65100", marginBottom: 8 }}>
                  ⚠ Select both events for journey stitchability. Without them, new events are auto-created.
                </div>
              )}

              {/* JOLT Mapping Editor */}
              {egressSchema && (
                <div style={{ ...S.card, borderColor: "#c8e6c9" }}>
                  <label style={{ ...S.label, color: "#1b2a4a", fontSize: 10 }}>
                    Visual JOLT Mapping — {egressSchema.name}
                  </label>
                  <div style={{ maxHeight: 250, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 6 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 24px 1.2fr 50px", gap: 2, padding: "4px 8px", background: "#f4f5f7", borderBottom: "1px solid #1b2a4a", fontSize: 9, fontWeight: 700, color: "#1b2a4a" }}>
                      <span>EGRESS FIELD</span><span></span><span>SOURCE</span><span>TYPE</span>
                    </div>
                    {egressSchemaFields.filter(f => !f.subFields).map((egressField, ei) => {
                      const currentSource = Object.entries(joltMappings).find(([,v]) => v === egressField.name);
                      const ingressOptions = ingressSchemaFields.filter(f => !f.subFields).map(f => f.name);
                      let parsedOut = {};
                      try { parsedOut = JSON.parse(outputSchema); } catch { /* invalid JSON while editing */ }
                      const agentOptions = Object.keys(parsedOut);
                      const selectedValue = currentSource ? currentSource[0] : "";
                      const sourceType = ingressOptions.includes(selectedValue) ? "ingress" : agentOptions.includes(selectedValue) ? "agent" : selectedValue ? "pass" : "none";
                      const badgeColors = { ingress: ["#e3f2fd","#1565c0"], agent: ["#e8f5e9","#2e7d32"], pass: ["#f3e5f5","#7b1fa2"], none: ["#ffebee","#c62828"] };
                      const [bg, fg] = badgeColors[sourceType] || badgeColors.none;
                      return (
                        <div key={egressField.name} style={{ display: "grid", gridTemplateColumns: "1fr 24px 1.2fr 50px", gap: 2, padding: "3px 8px", borderBottom: "1px solid #f0f0f0", background: sourceType === "none" ? "#fff8f8" : ei%2===0 ? "#fff" : "#fafafa", fontSize: 10 }}>
                          <span style={{ color: "#1b2a4a", paddingLeft: egressField.name.includes(".") ? 8 : 0 }}>{egressField.name.includes(".") ? "└ " : ""}{egressField.name.split(".").pop()}</span>
                          <span style={{ color: "#bbb", textAlign: "center" }}>←</span>
                          <select style={{ ...S.select, fontSize: 9, padding: "2px 4px", borderColor: sourceType === "none" ? "#ffcdd2" : "#d0d0d0" }} value={selectedValue}
                            onChange={e => { const nv = e.target.value; const nm = {...joltMappings}; for(const k of Object.keys(nm)){if(nm[k]===egressField.name)delete nm[k];} if(nv)nm[nv]=egressField.name; setJoltMappings(nm); rebuildJoltFromMappings(nm); }}>
                            <option value="">--</option>
                            <optgroup label="📥 Ingress">{ingressOptions.map(f => <option key={"i_"+f} value={f}>{f.split(".").pop()}</option>)}</optgroup>
                            <optgroup label="🤖 Agent">{agentOptions.map(f => <option key={"a_"+f} value={f}>{f}</option>)}</optgroup>
                          </select>
                          <span style={{ ...S.tag, background: bg, color: fg, fontSize: 8, textAlign: "center", padding: "1px 4px" }}>{sourceType}</span>
                        </div>
                      );
                    })}
                  </div>
                  <details style={{ marginTop: 4 }}>
                    <summary style={{ fontSize: 9, color: "#888", cursor: "pointer" }}>Raw JOLT JSON</summary>
                    <textarea style={{ ...S.textarea, minHeight: 60, fontFamily: "monospace", fontSize: 10, marginTop: 3 }}
                      value={egressJolt} onChange={e => setEgressJolt(e.target.value)} />
                  </details>
                </div>
              )}
            </div>
          )}

          {/* ══════════════ STEP 5: Test ══════════════ */}
          {wizardStep === 5 && (
            <div>
              <h3 style={{ color: "#1b2a4a", margin: "0 0 4px", fontSize: 15 }}>Test Agent</h3>
              <p style={{ color: "#888", fontSize: 12, margin: "0 0 12px" }}>Run the agent with a sample payload to verify its behavior before saving.</p>

              <div style={S.section}>
                <label style={S.label}>
                  Test Payload (ingress event data)
                  {ingressEvent && <span style={{ fontWeight: 400, color: "#e65100", fontSize: 9, marginLeft: 6 }}>from {ingressEvent}</span>}
                </label>
                <textarea style={{ ...S.textarea, minHeight: 100, fontFamily: "monospace", fontSize: 12 }}
                  value={testPayload} onChange={e => setTestPayload(e.target.value)} />
              </div>

              <button style={{ ...S.btn, ...S.btnSuccess, width: "100%", padding: "10px", marginBottom: 12 }}
                onClick={testAgent} disabled={testing || !agentName}>
                {testing ? "Running agent..." : "▶ Test Agent"}
              </button>

              {/* Test result */}
              {testResult && (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#1b2a4a" }}>Result</span>
                    <span style={{ ...S.tag,
                      background: testResult.success ? "#e8f5e9" : "#ffebee",
                      color: testResult.success ? "#2e7d32" : "#c62828"
                    }}>
                      {testResult.success ? "SUCCESS" : "FAILED"} — {testResult.executionTimeMs}ms
                    </span>
                  </div>

                  {/* Raw agent output */}
                  {testResult.result && (
                    <div style={{ ...S.card, borderColor: "#e6510033" }}>
                      <div style={{ ...S.label, color: "#e65100", fontSize: 10 }}>Agent Raw Output</div>
                      <div style={S.codeBlock}>{JSON.stringify(testResult.result, null, 2)}</div>
                    </div>
                  )}

                  {/* JOLT-transformed output */}
                  {testResult.result && egressJolt && (
                    <div style={{ ...S.card, borderColor: "#2e7d3244" }}>
                      <div style={{ ...S.label, color: "#2e7d32", fontSize: 10 }}>
                        📤 After JOLT Transform (egress event payload)
                      </div>
                      <div style={S.codeBlock}>{(() => {
                        let orig = {};
                        try { orig = JSON.parse(testPayload); } catch { /* invalid JSON while editing */ }
                        const merged = { ...orig, ...testResult.result, _agentDecision: testResult.result };
                        return JSON.stringify(applySimpleJolt(merged, egressJolt), null, 2);
                      })()}</div>
                      {egressEvent && (
                        <div style={{ fontSize: 10, color: "#888", marginTop: 3 }}>
                          This is what <b style={{ color: "#1b2a4a" }}>{egressEvent}</b> carries to the next service.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Reasoning trace */}
                  {testResult.trace?.steps && (
                    <div style={{ ...S.card, borderColor: "#1b2a4a22" }}>
                      <div style={{ ...S.label, color: "#1b2a4a", fontSize: 10 }}>
                        Reasoning Trace ({testResult.trace.iterations} iterations, tools: {testResult.trace.toolsUsed?.join(", ") || "none"})
                      </div>
                      {testResult.trace.steps.map((step, i) => (
                        <div key={i} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: "1px solid #f0f0f0" }}>
                          <div style={{ fontSize: 10, color: "#1b2a4a", fontWeight: 600, marginBottom: 3 }}>Iteration {step.iteration}</div>
                          {step.thought && <div style={{ fontSize: 11, color: "#555", marginBottom: 3 }}>💭 {step.thought}</div>}
                          {step.toolName && (
                            <>
                              <div style={{ fontSize: 11, color: "#e65100" }}>🔧 {step.toolName}({JSON.stringify(step.toolInput).substring(0, 80)}...)</div>
                              <div style={{ fontSize: 10, color: "#888", marginTop: 1 }}>
                                → {typeof step.toolOutput === "string" ? step.toolOutput.substring(0, 200) : JSON.stringify(step.toolOutput)?.substring(0, 200)}
                                {step.toolTimeMs && <span> ({step.toolTimeMs}ms)</span>}
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {testResult.error && (
                    <div style={{ ...S.card, borderColor: "#ffcdd2", background: "#fff8f8" }}>
                      <div style={{ ...S.label, color: "#c62828", fontSize: 10 }}>Error</div>
                      <div style={{ fontSize: 12, color: "#c62828" }}>{testResult.error}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ══════════════ STEP 6: Save ══════════════ */}
          {wizardStep === 6 && (
            <div>
              <h3 style={{ color: "#1b2a4a", margin: "0 0 16px", fontSize: 15 }}>Review & Save</h3>

              {/* Summary */}
              <div style={{ ...S.card, borderColor: "#c8e6c9", marginBottom: 12 }}>
                <div style={{ fontSize: 12, lineHeight: 1.8 }}>
                  <div><b>Agent:</b> {agentName || <span style={{ color: "#c62828" }}>Not set</span>}</div>
                  <div><b>Task:</b> {task} &nbsp;|&nbsp; <b>Max Iter:</b> {maxIter} &nbsp;|&nbsp; <b>Fail-open:</b> {failOpen ? "Yes" : "No"}</div>
                  <div><b>Instructions:</b> {instructions ? instructions.substring(0, 80) + "..." : <span style={{ color: "#c62828" }}>Not set</span>}</div>
                  <div><b>Tools:</b> {selectedTools.length > 0 ? selectedTools.map(t => `${t.name} (${t.type || "REST"})`).join(", ") : <span style={{ color: "#888" }}>None</span>}</div>
                  <div><b>Ingress:</b> {ingressEvent || <span style={{ color: "#888" }}>Auto-create</span>} → <b>Egress:</b> {egressEvent || <span style={{ color: "#888" }}>Auto-create</span>}</div>
                  <div><b>CIP Service:</b> <code style={{ color: "#2e7d32" }}>ai-agent-{agentName ? agentName.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") : "{name}"}</code></div>
                </div>
              </div>

              {/* Validation */}
              {(!agentName || !instructions) && (
                <div style={{ ...S.card, borderColor: "#ffcdd2", background: "#fff8f8", marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: "#c62828" }}>
                    {!agentName && <div>✗ Agent name is required (Step 1)</div>}
                    {!instructions && <div>✗ Instructions are required (Step 1)</div>}
                  </div>
                </div>
              )}

              <button style={{ ...S.btn, ...S.btnPrimary, width: "100%", padding: "12px", fontSize: 14, marginBottom: 8 }}
                onClick={saveAgent} disabled={saving || !agentName || !instructions}>
                {saving ? "Saving..." : "💾 Save Agent as CIP Service"}
              </button>

              {/* Save result */}
              {status && status.includes("Agent saved") && (
                <div style={{ ...S.card, borderColor: "#c8e6c9", background: "#f8fdf8", marginTop: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#2e7d32", marginBottom: 6 }}>✅ Save Successful</div>
                  <div style={{ fontSize: 12, color: "#333", lineHeight: 1.6 }}>
                    <div>{status}</div>
                    <div style={{ marginTop: 8, padding: "8px 12px", background: "#fff", border: "1px solid #e0e0e0", borderRadius: 6, fontSize: 11 }}>
                      <div><b>What happened:</b></div>
                      <div>1. Agent registered in runtime ✓</div>
                      <div>2. CIP service created: <code>ai-agent-{agentName.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}</code> ✓</div>
                      <div>3. Events wired: {ingressEvent || "auto"} → {egressEvent || "auto"} ✓</div>
                      <div>4. Service approved to ACTIVE ✓</div>
                      <div style={{ marginTop: 4, color: "#888" }}>Config persisted in CIP serviceDescription for reload.</div>
                    </div>
                  </div>
                </div>
              )}

              {status && (status.includes("CIX:") && !status.includes("✓")) && (
                <div style={{ ...S.card, borderColor: "#ffcdd2", background: "#fff8f8", marginTop: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#c62828", marginBottom: 6 }}>⚠ Save Issue</div>
                  <div style={{ fontSize: 12, color: "#c62828" }}>{status}</div>
                  <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Agent was registered in the runtime but CIP service creation may have failed. Check the backend console for details.</div>
                </div>
              )}

              {status && status.includes("Error:") && (
                <div style={{ ...S.card, borderColor: "#ffcdd2", background: "#fff8f8", marginTop: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#c62828", marginBottom: 6 }}>❌ Save Failed</div>
                  <div style={{ fontSize: 12, color: "#c62828" }}>{status}</div>
                </div>
              )}
            </div>
          )}

          {/* ── Navigation buttons ── */}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16, paddingTop: 12, borderTop: "1px solid #e8e8e8" }}>
            <button style={{ ...S.btn, ...S.btnGhost, visibility: wizardStep > 1 ? "visible" : "hidden" }}
              onClick={() => setWizardStep(s => Math.max(1, s - 1))}>
              ← Back
            </button>
            <span style={{ fontSize: 11, color: "#bbb", alignSelf: "center" }}>Step {wizardStep} of 6</span>
            <button style={{ ...S.btn, ...(wizardStep < 6 ? S.btnPrimary : S.btnGhost), visibility: wizardStep < 6 ? "visible" : "hidden" }}
              onClick={() => setWizardStep(s => Math.min(6, s + 1))}>
              Next →
            </button>
          </div>
        </div>


        {/* ══════════════ RIGHT PANEL — Preview ══════════════ */}
        <div style={S.panelR}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 20, color: "#1b2a4a" }}>
            Preview
          </div>

          {/* Agent summary card */}
          {agentName && (
            <div style={{ ...S.card, borderColor: "#c8e6c9", marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#1b2a4a", marginBottom: 6 }}>
                {agentName.startsWith("ai-agent") ? "🤖" : "🤖"} {agentName}
              </div>
              <div style={{ fontSize: 11, color: "#666", lineHeight: 1.6 }}>
                <div><b>Task:</b> {task} &nbsp;|&nbsp; <b>Tools:</b> {selectedTools.length} &nbsp;|&nbsp; <b>Max Iter:</b> {maxIter}</div>
                <div><b>Endpoint:</b> <code style={{ color: "#2e7d32" }}>/api/agent/{agentName.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}</code></div>
                {ingressEvent && <div><b>IN:</b> <span style={{ color: "#2e7d32" }}>{ingressEvent}</span> → <b>OUT:</b> <span style={{ color: "#c62828" }}>{egressEvent || "auto"}</span></div>}
                {instructions && <div style={{ marginTop: 4, color: "#888", fontStyle: "italic", fontSize: 10 }}>{instructions.substring(0, 120)}...</div>}
              </div>
            </div>
          )}

          {/* Ingress + Egress schema side by side */}
          {(ingressSchema || egressSchema) && (
            <div style={{ ...S.grid2, marginBottom: 16 }}>
              {/* Ingress schema */}
              <div style={{ ...S.card, borderColor: "#e6510033" }}>
                <label style={{ ...S.label, color: "#e65100", fontSize: 10 }}>
                  📥 Ingress: {ingressSchema ? ingressSchema.name : "Not selected"}
                </label>
                {ingressSchemaFields.length > 0 ? (
                  <div style={{ fontSize: 11, maxHeight: 200, overflowY: "auto" }}>
                    {ingressSchemaFields.filter(f => !f.subFields).map(f => (
                      <div key={f.name} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderBottom: "1px solid #e0e0e0" }}>
                        <span style={{ color: "#e65100", paddingLeft: f.name.includes(".") ? 12 : 0 }}>
                          {f.name.includes(".") ? "└ " + f.name.split(".").pop() : f.name}
                        </span>
                        <span style={{ color: "#555", fontSize: 10 }}>{f.type}</span>
                      </div>
                    ))}
                  </div>
                ) : <div style={{ fontSize: 11, color: "#555" }}>Select ingress event</div>}
              </div>

              {/* Egress schema */}
              <div style={{ ...S.card, borderColor: "#1b2a4a33" }}>
                <label style={{ ...S.label, color: "#1b2a4a", fontSize: 10 }}>
                  📤 Egress: {egressSchema ? egressSchema.name : "Not selected"}
                </label>
                {egressSchemaFields.length > 0 ? (
                  <div style={{ fontSize: 11, maxHeight: 200, overflowY: "auto" }}>
                    {egressSchemaFields.filter(f => !f.subFields).map(f => {
                      const mappedFrom = Object.entries(joltMappings).find(([,v]) => v === f.name);
                      return (
                        <div key={f.name} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderBottom: "1px solid #e0e0e0" }}>
                          <span style={{ color: "#1b2a4a", paddingLeft: f.name.includes(".") ? 12 : 0 }}>
                            {f.name.includes(".") ? "└ " + f.name.split(".").pop() : f.name}
                          </span>
                          <span style={{ color: mappedFrom ? "#2e7d32" : "#e65100", fontSize: 10 }}>
                            {mappedFrom ? `← ${mappedFrom[0]}` : "pass-through"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : <div style={{ fontSize: 11, color: "#555" }}>Select egress event</div>}
              </div>
            </div>
          )}

          {/* Tools summary */}
          {selectedTools.length > 0 && (
            <div style={S.section}>
              <label style={S.label}>Tools ({selectedTools.length})</label>
              {selectedTools.map((t, i) => {
                const typeColors = { REST: "#1565c0", DB_LOOKUP: "#e65100", RULES_ENGINE: "#7b1fa2", KNOWLEDGE_BASE: "#2e7d32" };
                const typeIcons = { REST: "🔧", DB_LOOKUP: "🗄️", RULES_ENGINE: "⚖️", KNOWLEDGE_BASE: "📚" };
                const toolType = t.type || "REST";
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 0", borderBottom: "1px solid #f0f0f0", fontSize: 11 }}>
                    <span>{typeIcons[toolType]}</span>
                    <span style={{ fontWeight: 500 }}>{t.name}</span>
                    <span style={{ ...S.tag, background: (typeColors[toolType] || "#999") + "15", color: typeColors[toolType], fontSize: 8 }}>{toolType}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Quick help */}
          <div style={{ ...S.card, borderColor: "#e0e0e0", marginTop: 20 }}>
            <div style={S.label}>Quick Guide</div>
            <div style={{ fontSize: 11, color: "#888", lineHeight: 1.8 }}>
              1. <b>Basics</b> — name, task type, instructions<br/>
              2. <b>Tools</b> — REST APIs, DB, rules, knowledge base<br/>
              3. <b>Schema</b> — output format + business context<br/>
              4. <b>Events</b> — ingress/egress events + JOLT mapping<br/>
              5. <b>Test</b> — run with sample payload, review results<br/>
              6. <b>Save</b> — register as CIP service
            </div>
            <div style={{ marginTop: 8 }}>
              <span style={{ fontSize: 11, color: "#1b2a4a", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                role="button" tabIndex={0} onClick={() => setCurrentTab("docs")} onKeyDown={activateOnKey(() => setCurrentTab("docs"))}>Full Documentation →</span>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* ── Status bar ── */}
      <div style={{ ...S.statusBar, color: status.startsWith("✓") ? "#2e7d32" : status.startsWith("✗") ? "#c62828" : "#888" }}>
        {status || "Ready"}
      </div>
    </div>
  );
};

export default CipAiAgentStudio;
